import GeometryType from 'ol/geom/GeometryType';
import LayerVector from 'ol/layer/Vector';
import SourceVector from 'ol/source/Vector';

import Mediator from 'gisw/Classes/Mediator';
// @ts-ignore
import auth from 'gisw/Helpers/auth';
import { HIGHLIGHT, PARCEL, POINT } from 'gisw/Helpers/StyleConstants';
import messageObserver, {
  messageManager
} from 'gisw/Widgets/MessageManager/MessageObserver';

import toolUrl from 'src/modules/tool-url';

/**
 * Defines handlers for all actions between eCounty widgets.
 *
 * Methods here should utilize methods provided by eCounty widgets.
 * If the logic of any single method becomes too large,
 * it may need to be refactored into methods defined
 * on the appropriate eCounty widget.
 *
 * ** Do not define eCounty specific modules
 * in Shared are used in all MagnumOpus sites **
 *
 * one string key for each "inter-widget" action utilized by a widget on the
 * eCounty site.  Keys may use "dot" notation to emulate namespacing of actions,
 * e.g. "tools.action1".
 * This object is used as the parameter of the {@link Mediator#registerHandlers}
 * method which uses each key to define a method
 * on the Maestro object {@link Mediator}.  One can then call the handler via
 * the {@link Mediator#handle} method.
 */

export function getHandlers () {
  const graphicsSource = new SourceVector();
  const graphicsLayer = new LayerVector({
    source: graphicsSource,
    renderOrder (featureA, featureB) {
      // Show features with PID ontop
      return +!!featureA.attributes.PID - +!!featureB.attributes.PID;
    }
  });

  return {
    loadStartUpTool () {
      const targetTool = toolUrl.getTool();

      if (targetTool !== null && Mediator.toolsManager.hasTool(targetTool)) {
        Mediator.handle({ name: 'openTool', args: [targetTool] });
        return;
      }

      if (Object.keys(Mediator.serverData.startupTools).length === 0) {
        return;
      }

      const hasStartupTools = Mediator.toolsManager.tabHasStartupTools();

      if (!auth.loggedIn && !hasStartupTools) {
        Mediator.login.show();
      } else if (!hasStartupTools) {
        messageObserver.broadcast({
          title: 'Warning',
          message: `Current user doesn't have access to the specified tool.`
        });
      } else {
        Mediator.toolsManager.bootStartupTools();
      }
    },

    /**
     * Open any main tool by clicking its domNode
     *
     * Note: This should **not** be so heavily tied to the dom
     * @param {FactoryTypes.ModuleNames} toolName
     */
    openTool (toolName) {
      const isMain = Mediator.centerMenu.toolsMain.style.display !== 'none';
      const toolContainer = isMain
        ? Mediator.centerMenu.toolsMain.firstElementChild
        : Mediator.centerMenu.toolsSecondary.firstElementChild;

      if (!toolContainer) {
        return;
      }

      /** @type {HTMLButtonElement} */
      const button = toolContainer.querySelector(
        `button[data-tool-name="${toolName}"]`
      );

      if (button) {
        button.click();
      }
    },

    'toolsManager.ActiveTool.Close': function () {
      Mediator.toolsManager.closeActiveTool(true);
    },

    /**
     * load individual tool domNode here
     * @param {*} tool
     * @param {FactoryTypes.ModuleNames} toolName
     * @param {boolean} hasContent
     */
    'toolsManagerMenu.button.click': function (tool, toolName, hasContent) {
      Mediator.jstManager.jstToolEvent(toolName, 'Open', null, null, null);
      Mediator.toolsManager.handleAlwaysOnIdentify(toolName);

      // @ts-ignore
      if (toolName !== 'Clear_') {
        Mediator.leftPanel.closeAllOpened();
      } else {
        if (Mediator.AlwaysOnIdentify) {
          Mediator.AlwaysOnIdentify.resume();
        }

        if (Mediator.isMobile || Mediator.inMobileMode) {
          Mediator.gWindowManager.destroyGWindow('activeTool');

          if (Mediator.mobileLeftVisible) {
            Mediator.hideMobileLeft();
          }

          if (Mediator.mobileSearchVisible) {
            Mediator.hideMobileRight();
          }
        }

        messageManager.toolLeaveScreen();
      }

      let toolGWin = Mediator.leftPanel.getGWindow('activeTool');
      const mobileTool = Mediator.gWindowManager.getGWindow('activeTool');

      if (!hasContent) {
        if (
          (Mediator.isMobile || Mediator.inMobileMode) &&
          !Mediator.desktopMode
        ) {
          Mediator.hideMobileLeft();
        }

        Mediator.activeTool = toolName;

        if (toolGWin) {
          toolGWin.destroy();
        }

        return;
      }

      const toolTitleDescription = Mediator.ToolsTitleDiscription[toolName]
        ? Mediator.ToolsTitleDiscription[toolName]
        : toolName || 'Tool';

      if (!toolGWin && !mobileTool) {
        if (
          (Mediator.isMobile || Mediator.inMobileMode) &&
          !Mediator.desktopMode
        ) {
          const { gWindow } = Mediator.gWindowManager.createGWindow({
            title: toolTitleDescription,
            id: toolTitleDescription + 'Panel',
            key: 'activeTool',
            'class': 'toolGWindow',
            content: tool.domNode,
            closeCallback: () => {
              Mediator.toolsManager.closeActiveTool(true);
            },
            showCloseButton: true,
            position: {
              bottom: '0',
              left: '0',
              width: '100%',
              maxWidth: '235px',
              zIndex: '1',
              position: 'absolute'
            }
          });

          document.body.appendChild(gWindow.domNode);

          Mediator.hideMobileLeft();

          return;
        }

        // Extracting `closeCallback` so that it doesn't skip
        // the `closeActiveTool` call
        /** @type {Partial<import('gisw/Widgets/GWindow').GWindowConfig>} */
        const { closeCallback = null, ...otherParams } =
          tool.GWindowParams ?? {};

        Mediator.leftPanel.addTool({
          title: toolTitleDescription,
          id: toolTitleDescription + 'Panel',
          key: 'activeTool',
          'class': 'toolGWindow',
          content: tool.domNode,
          closeCallback: key => {
            Mediator.toolsManager.closeActiveTool(true);

            if (closeCallback) {
              closeCallback(key);
            }
          },
          showCloseButton: true,
          ...otherParams
        });

        toolGWin = Mediator.leftPanel.getGWindow('activeTool');

        const msgBox = document.getElementById('emptyToolMessage');

        if (msgBox) {
          switch (toolTitleDescription) {
            case 'Zoom In':
              msgBox.innerHTML = 'Click and drag on the map to zoom in.';
              break;
            case 'Zoom Out':
              msgBox.innerHTML = 'Click and drag on the map to zoom out.';
              break;
            case 'Pan':
              msgBox.innerHTML = 'Click and drag on the map';
              msgBox.style.marginLeft = '20px';
              break;
          }
        }
      } else {
        Mediator.leftPanel.remove('activeTool');
        Mediator.gWindowManager.destroyGWindow('activeTool');

        if (Mediator.isMobile && !Mediator.desktopMode) {
          const { gWindow } = Mediator.gWindowManager.createGWindow({
            title: toolTitleDescription,
            id: toolTitleDescription + 'Panel',
            key: 'activeTool',
            'class': 'toolGWindow',
            content: tool.domNode,
            closeCallback: () => {
              Mediator.toolsManager.closeActiveTool(true);
            },
            showCloseButton: true,
            position: {
              bottom: '0',
              left: '0',
              width: '100%',
              maxWidth: '235px',
              zIndex: '1',
              position: 'absolute'
            }
          });

          document.body.appendChild(gWindow.domNode);
          Mediator.hideMobileLeft();

          return;
        }

        /** @type {Partial<import('gisw/Widgets/GWindow').GWindowConfig>} */
        const { closeCallback = null, ...otherParams } =
          tool.GWindowParams ?? {};

        Mediator.leftPanel.addTool({
          title: toolTitleDescription,
          id: toolTitleDescription + 'Panel',
          key: 'activeTool',
          'class': 'toolGWindow',
          content: tool.domNode,
          closeCallback: key => {
            Mediator.toolsManager.closeActiveTool(true);

            if (closeCallback) {
              closeCallback(key);
            }
          },
          showCloseButton: true,
          ...otherParams
        });

        const msgBox = document.getElementById('emptyToolMessage');

        if (msgBox) {
          switch (toolTitleDescription) {
            case 'Zoom In':
              msgBox.innerHTML = 'Click and drag on the map to zoom in.';
              break;
            case 'Zoom Out':
              msgBox.innerHTML = 'Click and drag on the map to zoom out.';
              break;
            case 'Pan':
              msgBox.innerHTML = 'Click and drag on the map';
              msgBox.style.marginLeft = '20px';
              break;
          }
        }
      }
    },

    /**
     * @param {object} tab
     * @param {string} tab.name
     */
    'centerMenu.tab.click': function (tab) {
      if (
        !Mediator.serverData.Tabs.includes(tab.name) ||
        tab.name === Mediator.currentTab
      ) {
        return;
      }

      /** @type {import('gisw/Widgets/GenericSearch/Search').default} */
      const searchBar = Mediator.rightMenu.searchBar;

      // MUST set the new current tab before switching to the new tab
      // especially for the map extent
      Mediator.currentTab = tab.name;

      // If loading the website with a startup Tool
      // disable special tools from booting
      const hasStartupTool =
        Object.keys(Mediator.serverData.startupTools).length > 0;

      const disableSpecialBootUp =
        toolUrl.getTool() !== null ||
        Mediator.currentTab === null && hasStartupTool;

      // For eCountyDemo
      if (Mediator.serverData.entityName.toLowerCase() === 'ecountydemo') {
        // Check Subscription for County or Tab
        const countyName = Mediator.getDemoCountyName();

        searchBar.ac.reset(countyName);

        // switch to new tab's map extent
        // eCounty ONLY
        Mediator.layermanager.zoomToCurrentLayerFullExtent(true);
      }

      const toolsMenuWidget = Mediator.toolsManager.getToolsMenuButtonsForTab(
        tab.name
      );

      Mediator.baseMapManager.switchBaseMapForTab();

      if (Mediator.leftPanel.isGWindowKey('tools')) {
        Mediator.leftPanel.setContent('tools', toolsMenuWidget.domNode);
        Mediator.leftPanel.setContent(
          'layers',
          Mediator.layermanager.getTOC(tab.name)
        );
        Mediator.leftPanel.setContent(
          'basemaps',
          Mediator.baseMapManager.getDomNode()
        );

        Mediator.centerMenu.copyTools();
      }

      if (Mediator.helppage) {
        Mediator.helppage.updateHelpInfo(tab.name);
      }

      Mediator.rightMenu.clearList();
      searchBar.setSearchPlaceHolder();
      Mediator.rightMenu.populateAdvancedSearch(tab.name);

      Mediator.centerMenu.setSelectListValue(tab.name);

      // close extra windows that are open
      // This forces tools to call bootup when changing tabs...
      const toolGWin = Mediator.leftPanel.getGWindow('activeTool');

      if (toolGWin) {
        // @ts-ignore
        Mediator.toolsManager.buttonHandler(null, 'Clear_');
      } else if (Mediator.toolsManager.activeToolName) {
        Mediator.toolsManager.shutdownActiveTool();
        messageManager.toolLeaveScreen();
        Mediator.toolsManager.activeToolName = null;
      }

      const resultGWin = Mediator.gWindowManager.getGWindow('resultsSidePanel');

      if (resultGWin) {
        resultGWin.close();
      }

      if (disableSpecialBootUp) {
        return;
      }

      if (Mediator.toolsManager.hasTool('RoadsProjectManagement', tab.name)) {
        Mediator.handle({ name: 'openTool', args: ['RoadsProjectManagement'] });
      }
    },

    /**
     * @param {string} [gNameInput]
     * @param {number} [timeout]
     */
    'gWindow.content.refresh': function (gNameInput, timeout) {
      let gName = gNameInput;

      if (!gName) {
        gName = 'activeTool';
      }

      if (Mediator.gWindowManager.getGWindow(gName)) {
        if (timeout) {
          Mediator.gWindowManager.getGWindow(gName).delayRefresh(timeout);
        } else {
          Mediator.gWindowManager.getGWindow(gName).refresh();
        }
      }
    },

    /**
     * @param {import('gisw/Widgets/GenericSearch/ResultSet').default} results
     * @param {boolean} zoomToGeometry
     * @param {boolean} [clearMap]
     */
    'search.results.show': async function (results, zoomToGeometry, clearMap) {
      // check if there's actually results...'
      if (results.categories.length === 0) {
        return;
      }

      await Mediator.resultsPanel.loadResults(results, zoomToGeometry);

      // show the panel
      Mediator.rightMenu.displayResults(null, clearMap);
    },

    /**
     * @param {HTMLElement} results
     */
    'results.set': function (results) {
      // show the panel
      Mediator.rightMenu.displayResults(results);
    },

    /**
     * @param {number} [timeout]
     */
    'results.gWindow.refresh': function (timeout = 300) {
      Mediator.rightMenu.gWindowList
        .getGWindow('resultsPanel')
        .delayRefresh(timeout);
    },

    'remove.activeTool': function () {
      Mediator.map.getTargetElement().style.height = window.innerHeight + 'px';
      Mediator.leftPanel.remove('activeTool');
    },

    'loading.show': function () {
      Mediator.rightMenu.showWaitCursor();
    },

    'loading.hide': function () {
      let isMapLoading = false;
      Mediator.map.getLayers().forEach(function (layer) {
        isMapLoading = isMapLoading || layer.getSource().loading;
      });

      if (!isMapLoading) {
        Mediator.rightMenu.hideWaitCursor();
      }
    },

    'rightmenu.clearAll': function () {
      if (!Mediator.rightMenu) {
        return;
      }

      /** @type {import('gisw/Widgets/GenericSearch/Search').default} */
      const searchBar = Mediator.rightMenu.searchBar;

      Mediator.rightMenu.clearList({
        keepSalesSearch: true,
        keepPaymentSearch: true,
        keepInstrumentSearch: true,
        keepQuickSearch: true
      });

      searchBar.clearSearchInput();

      Mediator.ClearGraphics();
    },
    'results.clear': function () {
      Mediator.rightMenu.hideResults();
    },

    /**
     * @param {HTMLElement} node
     * @param {Partial<CSSStyleDeclaration>} [position]
     */
    'results.add': function (node, position) {
      Mediator.rightMenu.addSubResult(node, position);
    },

    /**
     * @param {Array<import('ol').Feature>} featureArray
     * @param {boolean} zoomToExtent
     * @param {boolean} clearMap
     * @param {boolean} [forceZoom]
     */
    'map.showgeometry': function (
      featureArray,
      zoomToExtent,
      clearMap,
      forceZoom
    ) {
      let hasGeometry = false;

      // Make sure graphics layer is on top
      Mediator.map.removeLayer(graphicsLayer);
      Mediator.map.addLayer(graphicsLayer);

      if (clearMap) {
        Mediator.ClearGraphics(true);
      }

      for (let i = 0; i < featureArray.length; i++) {
        const geometry = featureArray[i].getGeometry();

        if (geometry) {
          hasGeometry = true;

          let style;

          if (featureArray[i].attributes['PID'] &&
          geometry.getType() !== 'Point') {
            style = PARCEL;
          } else if (geometry.getType() === 'Point') {
            style = POINT;
          } else {
            style = HIGHLIGHT;
          }

          const olFeature = featureArray[i];
          olFeature.setStyle(style);
          graphicsSource.addFeature(olFeature);
        }
      }

      if (!hasGeometry) {
        return;
      }

      const singlePoint =
        featureArray.length === 1 &&
        featureArray[0].getGeometry().getType() === GeometryType.POINT;

      if (singlePoint) {
        /** @type {import('ol/geom/Point').default} */
        // @ts-ignore
        const geom = featureArray[0].getGeometry();

        if (forceZoom) {
          Mediator.map.getView().animate({
            duration: 200,
            center: geom.getCoordinates(),
            zoom: 20
          });
        } else {
          Mediator.map.getView().animate({
            duration: 200,
            center: geom.getCoordinates()
          });
        }

        return;
      }

      if (zoomToExtent) {
        Mediator.map.getView().fit(graphicsSource.getExtent(), {
          nearest: true,
          duration: 200
        });
      }
    },

    'payment.panel.show': function (searchStrings, strDate, paymentType) {
      Mediator.paymentPanel.loadResults(searchStrings, strDate, paymentType);
    },

    'cart.panel.show': function () {
      Mediator.CartPanel.loadPanel();
    },

    'cart.panel.addToCart': function (parcelId, typeId) {
      Mediator.CartPanel.addToCart(parcelId, typeId);
    },

    /**
     * @param {string} url
     */
    'window.popup.open': function (url) {
      const newWindow = window.open(url);

      if (!newWindow) {
        messageObserver.broadcast({
          title: 'Pop-up Blocker Enabled',
          message: 'Please disable your pop-up blocker to view content.',
          duration: 15000
        });
      }
    },

    /**
     * @param {string} searchString
     */
    'search.executeSearch': function (searchString) {
      /** @type {import('gisw/Widgets/GenericSearch/Search').default} */
      const searchBar = Mediator.rightMenu.searchBar;
      searchBar.executeSearch(searchString);
    },

    /**
     * Adds Personal Property information into the `results` parameter,
     * only if the `PersonalProperty` tool is available.
     * If the tool is unavailable, the `results` parameter remains unchanged.
     * @param {import('gisw/Widgets/GenericSearch/ResultSet').default} results
     * @param {string} searchString
     */
    'mix.search.results.async': async function (results, searchString) {
      const hasPersonalProperty = Mediator.toolsManager.hasTool(
        'PersonalProperty',
        Mediator.getCurrentTabName()
      );

      if (hasPersonalProperty) {
        await Mediator.toolsManager.searchPersonalProperty(
          results,
          searchString
        );
      }

      return results;
    },

    zoomToSTR (section, township, range) {
      const hasMapNav = Mediator.toolsManager.hasTool(
        'MapNavigation',
        Mediator.getCurrentTabName()
      );

      if (hasMapNav) {
        Mediator.toolsManager._tools.MapNavigation.zoomToSTR(
          section,
          township,
          range
        );
      }
    }
  };
}

/**
 * @typedef {ReturnType<getHandlers>} HandlerMap
 */

/**
 * @typedef {keyof HandlerMap} HandlerNames
 */

/**
 * @template {HandlerNames} T
 * @typedef {Parameters<HandlerMap[T]>} HandlerParams
 */

const Handlers = { getHandlers };

export default Handlers;
